import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { SurveyMarkdownText } from '../../lib/survey-markdown'
import type { SurveyQuestion } from '../../lib/survey-question.types'
import { SURVEY_REQUIRED_QUESTION_MESSAGE } from '../../lib/survey-answer'
import { surveyQuestionAnswersContainerSx } from '../../lib/survey-question-layout'
import {
  surveyQuestionCardInnerSx,
  surveyQuestionDescriptionSliderSx,
  surveyQuestionDescriptionSx,
  surveyQuestionEyebrowSx,
  surveyQuestionPromptSx,
  surveyQuestionRequiredErrorSx,
  surveyQuestionRequiredMarkSx,
} from '../../styles/surveyExecutionStyles'

interface SurveyQuestionCardContentProps {
  readonly question: SurveyQuestion
  readonly questionIndex: number
  readonly children: ReactNode
  readonly showRequiredError?: boolean
  readonly requiredErrorMessage?: string
}

/** Swappable interior of the center question card — prompt + answer controls only. */
export function SurveyQuestionCardContent({
  question,
  questionIndex,
  children,
  showRequiredError = false,
  requiredErrorMessage = SURVEY_REQUIRED_QUESTION_MESSAGE,
}: SurveyQuestionCardContentProps) {
  const isSlider = question.type === 'slider'
  const hasDescription = Boolean(question.description)

  return (
    <Box sx={surveyQuestionCardInnerSx} data-testid="survey-question-card-content">
      <Typography component="p" sx={surveyQuestionEyebrowSx} data-testid="survey-question-eyebrow">
        {`QUESTION ${questionIndex}`}
      </Typography>
      <Typography
        component="h2"
        sx={surveyQuestionPromptSx}
        data-testid="survey-question-prompt"
      >
        <SurveyMarkdownText component="span" text={question.prompt} sx={{ display: 'inline' }} />
        {question.mandatory ? (
          <Box
            component="span"
            sx={{ ...surveyQuestionRequiredMarkSx, display: 'inline' }}
            aria-hidden
          >
            {'\u00a0*'}
          </Box>
        ) : null}
      </Typography>
      {hasDescription ? (
        <SurveyMarkdownText
          component="p"
          text={question.description ?? ''}
          sx={isSlider ? surveyQuestionDescriptionSliderSx : surveyQuestionDescriptionSx}
        />
      ) : null}
      <Box
        sx={surveyQuestionAnswersContainerSx(question)}
        data-testid={`survey-q-${question.id}`}
      >
        {children}
      </Box>
      {showRequiredError ? (
        <Typography
          component="p"
          role="alert"
          sx={surveyQuestionRequiredErrorSx}
          data-testid="survey-question-required-error"
        >
          {requiredErrorMessage}
        </Typography>
      ) : null}
    </Box>
  )
}
